import type { FileTypeEnum } from "@mobsuccess-devops/lcm-client/schemas/fileTypeEnum";
import type { MediaResultAdapter } from "@mobsuccess-devops/lcm-client/schemas/mediaResultAdapter";
import { mimeTypes } from "@mobsuccess-devops/react-shared/utils/mime-types";

import type { MediaFile } from "./media-file";

export function mapMedia(
  media: MediaResultAdapter,
): Extract<MediaFile, { status: "UNCHANGED" | "MODIFIED" }> {
  if (!media.source) {
    throw new Error("Media source is required");
  }

  if (media.source.type.startsWith("audio/")) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const type = media.source.type as Extract<FileTypeEnum, `audio/${string}`>;
    return {
      uuid: media.uuid,
      type,
      name: media.name,
      url: media.source.path,
      status: "UNCHANGED" as const,
      originalSource: media.source,
      originalCover: media.cover,
      placements: media.meta_placements,
    };
  }

  if (media.source.type.startsWith("image/")) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const type = media.source.type as Extract<FileTypeEnum, `image/${string}`>;
    return {
      uuid: media.uuid,
      type,
      name: media.name,
      status: "UNCHANGED" as const,
      originalSource: media.source,
      originalCover: media.cover,
      url: media.source.path,
      width: media.source.width ?? 0,
      height: media.source.height ?? 0,
      placements: media.meta_placements,
    };
  }

  if (media.source.type.startsWith("video/")) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const type = media.source.type as Extract<FileTypeEnum, `video/${string}`>;
    return {
      uuid: media.uuid,
      type,
      name: media.name,
      status: "UNCHANGED" as const,
      originalSource: media.source,
      originalCover: media.cover,
      url: media.source.path,
      width: media.source.width ?? 0,
      height: media.source.height ?? 0,
      cover: media.cover ? media.cover.path : null,
      placements: media.meta_placements,
    };
  }

  if (mimeTypes.zip().includes(media.source.type)) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const type = media.source.type as Extract<
      FileTypeEnum,
      `application/${string}zip${string}`
    >;
    return {
      uuid: media.uuid,
      type,
      name: media.name,
      status: "UNCHANGED" as const,
      url: media.source.path,
      originalSource: media.source,
      originalCover: media.cover,
      placements: media.meta_placements,
    };
  }

  if (media.source.type === "dco/video") {
    // declinaison_uuid is returned by LCM once exposed on MediaResult; keep
    // optional until the OpenAPI client is regenerated.
    const declinaisonUuid =
      "declinaison_uuid" in media && media.declinaison_uuid != null
        ? String(media.declinaison_uuid)
        : undefined;
    return {
      uuid: media.uuid,
      name: media.name,
      url: media.dco_preview_url ?? media.source.path,
      type: "dco/video" as const,
      status: "UNCHANGED" as const,
      originalSource: media.source,
      originalCover: media.cover,
      width: media.source.width ?? 0,
      height: media.source.height ?? 0,
      duration: media.source.duration ?? 0,
      cover: media.cover ? media.cover.path : null,
      placements: media.meta_placements,
      ...(declinaisonUuid ? { declinaison: declinaisonUuid } : {}),
      ...(media.source.composition_uuid
        ? { composition: String(media.source.composition_uuid) }
        : {}),
    };
  }

  if (media.source.type === "youtube/video") {
    return {
      uuid: media.uuid,
      name: media.name,
      url: media.source.path,
      type: "youtube/video" as const,
      status: "UNCHANGED" as const,
      originalSource: media.source,
      originalCover: media.cover,
      width: media.source.width ?? 0,
      height: media.source.height ?? 0,
      duration: media.source.duration ?? 0,
      cover: media.cover ? media.cover.path : null,
      placements: media.meta_placements,
    };
  }

  if (media.source.type === "dco/static") {
    const declinaisonUuid =
      "declinaison_uuid" in media && media.declinaison_uuid != null
        ? String(media.declinaison_uuid)
        : undefined;
    return {
      uuid: media.uuid,
      name: media.name,
      url: media.dco_preview_url ?? media.source.path,
      type: "dco/static" as const,
      status: "UNCHANGED" as const,
      originalSource: media.source,
      originalCover: media.cover,
      width: media.source.width ?? 0,
      height: media.source.height ?? 0,
      placements: media.meta_placements,
      ...(declinaisonUuid ? { declinaison: declinaisonUuid } : {}),
    };
  }

  throw new Error(`Unsupported media type: ${media.source.type}`);
}
